import type { LabeledValuePayload } from '@Application/Users/UserStatuses/Actions/userStatusesFetched';

export type IdPayload = {
	readonly id: number;
};

export type PreviousStepDocumentsMode = 'all' | 'none' | 'selected';

export type AutomaticSourcesMode = 'all' | 'selected';

/** A document type reused as input of a preparation: `verifiedOnly` restricts it to verified documents. */
export type WorkflowAutomaticSourcePayload = {
	readonly documentType: IdPayload;
	readonly verifiedOnly: boolean;
};

/** Null for the automatic transcription, which has no preparation. */
export type WorkflowPreparationSettingsPayload = {
	readonly acceptsInterviews: boolean;
	readonly acceptsNotes: boolean;
	readonly acceptsDocuments: boolean;
	readonly requiresValidationBeforeNext: boolean;
	readonly automaticSources: readonly WorkflowAutomaticSourcePayload[];
};

/** Output side: document types are relations, rendered as `{ id }`. The save DTO sends bare ids instead. */
export type WorkflowStepInputsPayload = {
	readonly preparationElements: boolean;
	readonly previousStepDocumentsMode: PreviousStepDocumentsMode;
	readonly previousStepDocumentTypes: readonly IdPayload[];
	readonly automaticSourcesMode: AutomaticSourcesMode;
	readonly automaticSourceDocumentTypes: readonly IdPayload[];
};

export type WorkflowStepPromptsPayload = {
	readonly systemPrompt: string;
	readonly contextPrompt: string;
	readonly isSystemPromptPersonalized: boolean;
	readonly isContextPromptPersonalized: boolean;
};

export type WorkflowStepReevaluationPayload = WorkflowStepPromptsPayload & {
	readonly enabled: boolean;
};

export type WorkflowStepPayload = {
	readonly id: number;
	readonly position: number;
	/** Zero-indexed. */
	readonly stage: number;
	readonly type: LabeledValuePayload;
	readonly label: string;
	readonly documentType: IdPayload | null;
	readonly usage: LabeledValuePayload | null;
	readonly inputs: WorkflowStepInputsPayload | null;
	readonly prompts: WorkflowStepPromptsPayload | null;
	readonly reevaluation: WorkflowStepReevaluationPayload | null;
	readonly splitCalls: boolean;
	readonly sectionDependencies: Record<string, string[]>;
	readonly temperature: number | null;
	readonly vocabularyTerms: readonly string[] | null;
	readonly speakers: boolean | null;
};

/** One current version of a workflow, as rendered by `WorkflowResponse`. */
export type OrganizationWorkflowPayload = {
	readonly key: string;
	readonly version: number;
	readonly publishedAt: string;
	readonly label: string;
	readonly description: string | null;
	readonly launchMode: LabeledValuePayload;
	readonly isActive: boolean;
	readonly preparation: WorkflowPreparationSettingsPayload | null;
	readonly steps: readonly WorkflowStepPayload[];
	/** Only meaningful when a step is of type `speaker_separation`. */
	readonly separatesSpeakers: boolean;
};

export type OrganizationWorkflowsFetchedAction = {
	type: 'ORGANIZATION_WORKFLOWS_FETCHED';
	// `OrganizationWorkflowPayload[]`, never `readonly …[]`: synapse's `DefaultPayload` requires an
	// `Array<Record<string, unknown>>`.
	payload: OrganizationWorkflowPayload[];
	organizationId: number;
};

export default (
	payload: OrganizationWorkflowPayload[],
	organizationId: number,
): OrganizationWorkflowsFetchedAction => ({
	type: 'ORGANIZATION_WORKFLOWS_FETCHED',
	payload,
	organizationId,
});
